import { useFloating, useInteractions, useClick, offset, autoUpdate, autoPlacement } from '@floating-ui/react';
import { useResizeObserver } from 'usehooks-ts';
import { useCallback, useRef, FocusEventHandler } from 'react';

export function useDropdownFloating() {
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Setup floating UI
  const { refs, floatingStyles, context } = useFloating({
    strategy: 'fixed',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(6),
      autoPlacement({
        allowedPlacements: ['top', 'bottom'],
      }),
    ],
  });

  // Setup interactions
  const click = useClick(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click]);

  // Setup resize observer for width matching
  const { width = 0 } = useResizeObserver({
    ref: wrapperRef,
    box: 'border-box',
  });

  // Create blur handler function that returns a proper event handler
  const createBlurHandler = useCallback(
    (onBlur?: () => void): FocusEventHandler<HTMLButtonElement> => {
      return (e) => {
        // Don't call onBlur if the focus is moving to the floating element
        if (e.relatedTarget && e.relatedTarget === refs.floating.current) {
          return;
        }
        onBlur?.();
      };
    },
    [refs.floating],
  );

  return {
    wrapperRef,
    floatingRefs: refs,
    floatingStyles,
    floatingWidth: width,
    getReferenceProps,
    getFloatingProps,
    createBlurHandler,
  };
}
